import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useContext, useRef, useState } from "react";
import CampaignContext from "../../../contexts/CampaignContext";
import restService from "../../../restService";
import UserContext from "../../../contexts/UserContext";
import { parse } from "papaparse";

const downloadFile = new URL("../../../assets/Sample.csv", import.meta.url);

const AddStation = () => {
  const [campaign] = useContext(CampaignContext);
  const [user] = useContext(UserContext);
  const [parsedData, setParsedData] = useState([]);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChooseFile = () => {
    fileRef.current.click();
  };

  const navigate = useNavigate();

  const onAddStation = async (values) => {
    setIsSaving(true);
    
    try {
      const newStation = {
        name: values.stationName,
        location: values.stationName,
        zone: values.zone,
        agentMsisdn: values.agentPhone,
        region: values.region,
        campaignId: campaign.id,
      };

      await restService.Management.createStation(user.accessToken, newStation);
      navigate("/management/station");
    } catch (error) {
      console.log(error);
      setIsSaving(false);
    }
  };

  const stationForm = useFormik({
    initialValues: {
      stationName: "",
      stationPhone: "",
      zone: "",
      region: "",
      agentPhone: "",
    },
    validationSchema: Yup.object({
      stationName: Yup.string().required("Station location is required"),
      stationPhone: Yup.string()
        .required("Station phone is required")
        .matches(/^[0-9]+$/, "Must be only digits")
        .length(12, "Phone number must start with 233 & be 12 digits"),
      zone: Yup.string().required("Station zone is required"),
      region: Yup.string().required("Agent name is required"),
      agentPhone: Yup.string()
        .required("Station phone is required")
        .matches(/^[0-9]+$/, "Must be only digits")
        .length(12, "Phone number must start with 233 & be 12 digits"),
    }),
    onSubmit: onAddStation,
  });

  /**
   * Filters out empty and undefined items from an array of objects.
   * @param {Array} data - The array of objects to filter.
   * @returns {Array} - The filtered array of objects.
   */
  function filterEmptyAndUndefinedItems(data) {
    return data.filter((item) => {
      return Object.values(item).some((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined;
      });
    });
  }

  /**
   * Cleans the data by removing any leading/trailing whitespaces and newlines from the keys and values of each object in the array.
   * @param {Array} data - The array of objects to be cleaned.
   * @returns {Array} - The cleaned array of objects.
   */
  function cleanData(data) {
    return data.map((obj) => {
      const cleanedObj = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const cleanedKey = key.trim();
          const cleanedValue = obj[key].replace(/\n/g, "").trim();
          cleanedObj[cleanedKey] = cleanedValue;
        }
      }
      return cleanedObj;
    });
  }

  /**
   * Removes empty objects from an array of objects.
   * @param {Array} data - The array of objects to filter.
   * @returns {Array} - The filtered array of objects.
   */
  function removeEmptyObjects(data) {
    return data?.filter((obj) => {
      return Object.values(obj)?.some((value) => value?.trim() !== "");
    });
  }

  /**
   * Handles decoding of CSV file
   * @param {Object} e - The event object
   */
  const handleDecodeCSVFile = (e) => {
    const file = e?.target?.files[0];
    try {
      setFileName(file?.name);
      parse(file, {
        header: true,
        complete: function (results) {
          const res = results?.data;

          const items = cleanData(res);
          const filteredItems = removeEmptyObjects(items);

          const updatedKeys = filteredItems?.slice()?.map((item) => {
            const agentArray = item?.["Agents number"]?.split(",") || [];
            const adminArray = item?.["Admins number"]?.split(",") || [];
            let agentMsisdns = (agentArray || [])?.filter(
              (val) => val?.trim() !== ""
            );

            let adminMsisdns = (adminArray || [])?.filter(
              (val) => val?.trim() !== ""
            );

            return {
              location: item?.Location,
              name: item?.Name,
              region: item?.Region,
              zone: item?.Zone,
              agentMsisdns: (agentMsisdns || [])
                ?.slice()
                ?.map((x) => x?.trim()),
              adminMsisdns: (adminMsisdns || [])
                ?.slice()
                ?.map((x) => x?.trim()),
            };
          });

          const filteredData = filterEmptyAndUndefinedItems(updatedKeys);

          setParsedData(filteredData);
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleBulkAddStation = async () => {
    setIsSaving(true);
    try {
      await restService?.Management?.bulkAdd(campaign?.id, parsedData);

      navigate("/management/station");
    } catch (error) {
      console.log(error);
      setIsSaving(false);
    }
  };

  return (
    <div className=" md:mb-8 md:pb-8 px-4 w-full md:px-12">
      <div>
        <div className="flex items-center">
          <button className="mr-6" onClick={() => navigate(-1)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="text-xl">Stations / Add New Station</h1>
        </div>
      </div>

      <div className=" h-full pb-8   my-8 w-full">
        <div className="bg-white mb-12 ring-1 ring-gray-200 rounded-md shadow-[0_0_10px_-3px_rgba(0,0,0,0.2)] h-full">
          <div className="p-8 h-full">
            {parsedData.length < 1 ? (
              <div className="flex flex-col gap-4">
                <div
                  onClick={handleChooseFile}
                  className="border-dashed h-40 bg-blue-100	 border border-blue-400 border-dash rounded-lg "
                >
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <button className="flex items-center text-lg text-[#3989DD]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 mr-2 text-[#3989DD]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>{" "}
                        Upload CSV file
                      </button>
                      <p className="text-gray-400 text-sm">
                        Max. File Size: 10MB
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <a
                    href={downloadFile}
                    className="text-blue-500 underline underline-offset-2"
                    // download
                  >
                    Download a sample CSV file
                  </a>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-6">
                  <p>{fileName}</p>
                  <button
                    onClick={() => {
                      setFileName("");
                      setParsedData([]);
                    }}
                    className="text-[#ff0000]"
                    text-sm
                  >
                    Delete
                  </button>
                </div>
                <p>
                  Total:
                  {parsedData.length}
                </p>

                <div>
                  <div className="mt-12">
                    <button
                      onClick={handleBulkAddStation}
                      className="flex  text-base items-center  border text-white rounded-md py-2 px-16 bg-[#3989DD]"
                    >
                      Add Station
                    </button>
                  </div>
                </div>
              </div>
            )}

            <input
              type="file"
              onChange={handleDecodeCSVFile}
              value={""}
              hidden
              accept=".csv"
              ref={fileRef}
            />

            {parsedData.length < 1 && (
              <form className=" h-full" onSubmit={stationForm.handleSubmit}>
                {/* <div className="my-4">
                <button className="my-4 py-1.5 px-4 rounded-md text-red-400 ring-1 ring-red-400 hover:bg-red-400 hover:text-white">
                  Download sample CSV file
                </button>
              </div> */}

                <div className="mt-6">
                  {/* <div>
                  <p>You can also add stations or outlets manually</p>
                </div> */}
                  <div className="md:grid md:grid-cols-3 md:gap-16 ">
                    <div className="my-4">
                      <label htmlFor="stationName">
                        <p className="py-1 after:content-['*'] after:ml-1.5 after:text-red-500">
                          Station Location
                        </p>
                        <input
                          placeholder="Enter Station Name"
                          name="stationName"
                          onChange={stationForm.handleChange}
                          onBlur={stationForm.handleBlur}
                          value={stationForm.values.stationName}
                          className="w-full border border-gray-300 px-3 pr-8 invalid:border-red-500
                            invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500
                            py-1.5 rounded-md focus:outline-none"
                        />
                        {stationForm.errors.stationName &&
                          stationForm.touched.stationName && (
                            <p className="mt-1 text-red-400 text-xs">
                              {stationForm.errors.stationName}
                            </p>
                          )}
                      </label>
                    </div>
                    <div className="my-4">
                      <label htmlFor="stationPhone">
                        <p className="py-1 after:content-['*'] after:ml-1.5 after:text-red-500">
                          Station Phone Number
                        </p>
                        <input
                          placeholder="Station Phone ( 233 XX XXX XXXX ) "
                          name="stationPhone"
                          onChange={stationForm.handleChange}
                          value={stationForm.values.stationPhone}
                          onBlur={stationForm.handleBlur}
                          className="w-full border border-gray-300 px-3 pr-8 invalid:border-red-500
                            invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500
                            py-1.5 rounded-md focus:outline-none"
                        />
                        {stationForm.errors.stationPhone &&
                          stationForm.touched.stationPhone && (
                            <p className="mt-1 text-red-400 text-xs">
                              {stationForm.errors.stationPhone}
                            </p>
                          )}
                      </label>
                    </div>
                    <div className="my-4">
                      <label htmlFor="zone">
                        <p className="py-1 after:content-['*'] after:ml-1.5 after:text-red-500">
                          Zone
                        </p>
                        <input
                          placeholder="Enter Station Zone"
                          name="zone"
                          onBlur={stationForm.handleBlur}
                          onChange={stationForm.handleChange}
                          value={stationForm.values.zone}
                          className="w-full border border-gray-300 px-3 invalid:border-red-500
                            invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500
                           pr-8 py-1.5 rounded-md focus:outline-none"
                        />
                        {stationForm.errors.zone &&
                          stationForm.touched.zone && (
                            <p className="mt-1 text-red-400 text-xs">
                              {stationForm.errors.zone}
                            </p>
                          )}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="py-1.5">
                    <p className="text-xl ">Assign Agent</p>
                  </div>
                  <div className="md:grid md:grid-cols-3 md:gap-16 ">
                    <div className="my-4">
                      <p className="py-1 after:content-['*'] after:ml-1.5 after:text-red-500">
                        Region
                      </p>
                      <label htmlFor="region" className="relative block">
                        <select
                          placeholder="Enter Station Name"
                          name="region"
                          onChange={stationForm.handleChange}
                          value={stationForm.values.region}
                          className="w-full border border-gray-300 px-3 pr-8 invalid:border-red-500 appearance-none
                            invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500
                            py-1.5 rounded-md focus:outline-none"
                        >
                          <option value="" />
                          <option value="Ahafo">Ahafo Region</option>
                          <option value="Ashanti">Ashanti Region</option>
                          <option value="Bono East">Bono East</option>
                          <option value="Brong Ahafo">Brong Ahafo</option>
                          <option value="Central">Brong Ahafo</option>
                          <option value="Eastern">Eastern Region </option>
                          <option value="Greater Accra">
                            Greater Accra Region{" "}
                          </option>
                          <option value="North East">North East Region </option>
                          <option value="Northern">Northern Region </option>
                          <option value="Oti">Oti Region </option>
                          <option value="Savannah">Savannah Region </option>
                          <option value="Upper East">Upper East Region </option>
                          <option value="Upper West">Upper West Region </option>
                          <option value="Western">Western Region </option>
                          <option value="Western North">
                            Western North Region{" "}
                          </option>
                          <option value="Volta">Volta Region </option>
                        </select>
                        {stationForm.errors.region &&
                          stationForm.touched.region && (
                            <p className="mt-1 text-red-400 text-xs">
                              {stationForm.errors.region}
                            </p>
                          )}
                      </label>
                    </div>
                    <div className="my-4">
                      <label htmlFor="agentPhone">
                        <p className="py-1 after:content-['*'] after:ml-1.5 after:text-red-500">
                          Agent Phone Number
                        </p>
                        <input
                          placeholder="Agent Phone ( 233 XX XXX XXXX ) "
                          name="agentPhone"
                          onChange={stationForm.handleChange}
                          value={stationForm.values.agentPhone}
                          className="w-full border border-gray-300 px-3 pr-8 invalid:border-red-500
                            invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500
                            py-1.5 rounded-md focus:outline-none"
                        />
                        {stationForm.errors.agentPhone &&
                          stationForm.touched.agentPhone && (
                            <p className="mt-1 text-red-400 text-xs">
                              {stationForm.errors.agentPhone}
                            </p>
                          )}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-12">
                  {isSaving ? (
                    <button
                      disabled
                      className="py-2 hover:bg-blue-50 hover:border-blue-500 hover:border-2 hover:text-blue-500 hover:font-bold  px-16 rounded-md text-white bg-blue-500"
                    >
                      Adding Station...
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="py-2 hover:bg-blue-50 hover:border-blue-500 hover:border-2 hover:text-blue-500 hover:font-bold  px-16 rounded-md text-white bg-blue-500"
                    >
                      Add Station
                    </button>
                  )}

                  {/* <button
                    type="submit"
                    className="flex  text-base items-center bg-[#3989DD] border text-white rounded-md py-2 px-16
                         hover:border-[#3989DD] hover:bg-[#4891db] "
                  >
                    Add Station
                  </button> */}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStation;
